import { useState, useEffect } from "react";
import { useTheme } from "../ThemeProvider";
import api from "../../services/api";
import { useDraggableModalPanel } from "../../hooks/useDraggableModalPanel";

const wa = { green: "#16a34a", greenFrom: "#15803d", greenTo: "#16a34a", greenText: "#bbf7d0", greenShadow: "rgba(22,163,74,0.35)" };

export default function ModalEnviarLocalizacion({ isOpen, onClose, propiedadIds }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const [telefono, setTelefono] = useState("");
  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [lineas, setLineas] = useState([]);
  const { dragRef, startDrag, panelPositionStyle } = useDraggableModalPanel();

  useEffect(() => {
    if (!isOpen || !propiedadIds?.length) return;
    let cancel = false;
    (async () => {
      setCargando(true);
      setError("");
      try {
        const results = await Promise.all(
          propiedadIds.map((id) => api.get(`/propiedades/${id}`).then((r) => r.data.data || r.data).catch(() => null))
        );
        if (cancel) return;
        const ls = [];
        results.forEach((p, i) => {
          if (!p?.id) return;
          const lat = p.latitud != null ? Number(p.latitud) : null;
          const lng = p.longitud != null ? Number(p.longitud) : null;
          const ref = p.referencia || "";
          const titulo = p.titulo || "Propiedad";
          let url = "";
          if (lat && lng && !Number.isNaN(lat) && !Number.isNaN(lng)) {
            url = `https://www.google.com/maps?q=${lat},${lng}`;
          } else {
            const parts = [p.nombre_via, p.numero, p.poblacion || p.codigo_postal].filter(Boolean);
            const q = encodeURIComponent(parts.join(", ") || titulo);
            url = `https://www.google.com/maps/search/?api=1&query=${q}`;
          }
          ls.push({ id: p.id, ref, titulo, url });
        });
        setLineas(ls);
      } catch (e) {
        if (!cancel) setError("No se pudieron cargar los datos de la propiedad");
      } finally {
        if (!cancel) setCargando(false);
      }
    })();
    return () => { cancel = true; };
  }, [isOpen, propiedadIds]);

  if (!isOpen) return null;

  const mensaje =
    lineas.length === 0
      ? ""
      : lineas.length === 1
        ? `📍 *Localización* — ${lineas[0].titulo}${lineas[0].ref ? ` (Ref. ${lineas[0].ref})` : ""}\n${lineas[0].url}`
        : `📍 *Localizaciones*\n${lineas.map((l) => `• ${l.titulo}${l.ref ? ` (${l.ref})` : ""}\n${l.url}`).join("\n\n")}`;

  const enviar = async () => {
    const digits = telefono.replace(/\D/g, "");
    if (digits.length < 9) {
      setError("Introduce un número de teléfono válido");
      return;
    }
    if (!mensaje) {
      setError("No hay ubicación que enviar");
      return;
    }
    setEnviando(true);
    setError("");
    try {
      const { data } = await api.post("/whatsapp/enviar", { telefono: digits, mensaje });
      if (!data.ok) {
        setError(data.error || "Error al enviar");
        setEnviando(false);
        return;
      }
      setEnviando(false);
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.error || "No se pudo conectar con el servidor");
      setEnviando(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        ref={dragRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 440,
          maxWidth: "94vw",
          maxHeight: "90vh",
          overflow: "auto",
          background: isDark ? c.bgCard : '#c8c8be',
          borderRadius: 16,
          border: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
          boxShadow: isDark
            ? "0 0 40px rgba(0,0,0,0.5)"
            : "0 16px 48px rgba(0,0,0,0.30), 0 4px 12px rgba(0,0,0,0.15)",
          ...panelPositionStyle,
        }}
      >
        <div style={{ height: 3, background: `linear-gradient(90deg, ${wa.greenTo}, ${wa.green})` }} />
        <div
          onMouseDown={startDrag}
          style={{
            padding: "14px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "grab",
            userSelect: "none",
            background: isDark ? "transparent" : "#e0e0d8",
            borderBottom: `1px solid ${isDark ? c.border : "#a8a8a0"}`,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: c.textPrimary }}>Enviar localización por WhatsApp</h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: `1px solid ${c.border}`,
              background: c.bgInput,
              cursor: "pointer",
              color: c.textMuted,
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ padding: "0 22px 18px" }}>
          <p style={{ margin: "0 0 12px", fontSize: 13, color: c.textMuted, lineHeight: 1.45 }}>
            Se enviará un mensaje con enlace a Google Maps (coordenadas si existen; si no, búsqueda por dirección).
          </p>
          {cargando && <p style={{ color: c.textMuted, fontSize: 13 }}>Cargando datos…</p>}
          {!cargando && lineas.length > 0 && (
            <ul style={{ margin: "0 0 14px", paddingLeft: 18, fontSize: 12, color: c.textSecondary }}>
              {lineas.map((l) => (
                <li key={l.id} style={{ marginBottom: 6 }}>
                  {l.titulo} {l.ref && <span style={{ color: c.textMuted }}>({l.ref})</span>}
                </li>
              ))}
            </ul>
          )}
          <label style={{ display: "block", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: c.textMuted, marginBottom: 8 }}>
            Teléfono WhatsApp
          </label>
          <input
            type="tel"
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
            placeholder="Ej. 34600111222"
            style={{
              width: "100%",
              padding: "11px 14px",
              borderRadius: 10,
              border: `2px solid ${c.border}`,
              background: c.bgInput,
              color: c.textPrimary,
              fontSize: 14,
              marginBottom: 12,
              boxSizing: "border-box",
            }}
          />
          {error && (
            <p style={{ color: "#ef4444", fontSize: 12, fontWeight: 600, margin: "0 0 12px" }}>{error}</p>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "10px 20px",
                borderRadius: 10,
              border: `1.5px solid ${isDark ? c.border : "#a8a8a0"}`,
              background: isDark ? c.bgInput : "#ffffff",
              color: c.textPrimary,
              cursor: "pointer",
              fontWeight: 600,
            }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={enviando || cargando || !lineas.length}
              onClick={enviar}
              style={{
                padding: "10px 22px",
                borderRadius: 10,
                border: "none",
                background: `linear-gradient(135deg, ${wa.greenFrom}, ${wa.greenTo})`,
                color: wa.greenText,
                cursor: enviando ? "wait" : "pointer",
                fontWeight: 700,
                boxShadow: `0 4px 14px ${wa.greenShadow}`,
              }}
            >
              {enviando ? "Enviando…" : "Enviar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
